/* eslint-disable complexity */
import { Field, FieldProps, useFormikContext } from 'formik';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { IconButton } from 'unbrace/components';
import { FormikDatePicker, Input } from '../';
import { MetaDataAccess, MetaDataValue, UseMetaDataActions } from '../../types/metadata';

import { legacySqlServerDateParser } from 'helpers';
import NumberInput from 'components/Form/NumberInput';
import { ContractEditFormValues } from 'modules/contract/types/contractDetail';
import { FormikSelect } from '../Form';
import { InputProps, TYPE_CHECKBOX, TYPE_PERCENTAGE, TYPE_TEXT } from '../Form/Input';
import { Delete } from '../icons';
import { ElementWithButton } from './ElementWithButton';

// Metadata values are stored as strings and not always in a format `new Date` can parse
// (e.g. SQL Server style "May  7 2025  6:17PM"); an Invalid Date crashes the date picker
const parseMetaDataDate = (value?: string | Date): Date | '' => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (!isNaN(date.getTime())) {
    return date;
  }
  const legacyDate = typeof value === 'string' ? legacySqlServerDateParser(value) : undefined;

  return legacyDate || '';
};

type Props = {
  disabled?: boolean;
  metaData: MetaDataValue;
  index: number;
  inputProps?: Partial<InputProps>;
  metaDataActions?: UseMetaDataActions;
  isModal?: boolean;
  hideDeleteButton?: boolean;
};

export const MetaDataField: React.FC<Props> = ({
  disabled,
  metaData,
  index,
  inputProps,
  metaDataActions,
  isModal,
  hideDeleteButton,
}: Props) => {
  const { setFieldValue, values } = useFormikContext<Pick<ContractEditFormValues, 'metadatas'>>();
  const { t } = useTranslation('metaData');
  const options = metaData.availableValues
    ? Object.entries(metaData.availableValues).map(([key, md]) => ({
        label: md || key || '',
        value: key,
      }))
    : [];

  const handleRemoveClick = () => {
    setFieldValue(
      'metadatas',
      values.metadatas.filter((md) => md.metadataId !== metaData.metadataId)
    );
    metaDataActions?.toggleMetaData(metaData.metadataId.toString());
  };

  const metaDataType = metaData.dataType?.toUpperCase();

  return (
    <Field name={`metadatas.${index}.value`}>
      {(fieldProps: FieldProps) => {
        return (
          <ElementWithButton>
            {metaDataType === 'MULTISELECT' ? (
              <FormikSelect
                {...fieldProps}
                label={t(`metaData:${metaData.code?.toUpperCase()}`) || ''}
                autoFocus={metaData.isNew}
                isDisabled={metaData.accessibility !== MetaDataAccess.Editable || disabled}
                options={options}
                error={inputProps?.error}
              />
            ) : metaDataType === 'BOOL' ? (
              <Input
                {...inputProps}
                {...fieldProps.field}
                value={fieldProps?.field?.value?.toString()?.toLowerCase() || 'false'}
                checked={fieldProps?.field?.value?.toString()?.toLowerCase() === 'true'}
                label={t(`metaData:${metaData.code?.toUpperCase()}`) || ''}
                disabled={metaData.accessibility !== MetaDataAccess.Editable || disabled}
                type={TYPE_CHECKBOX}
              />
            ) : metaDataType === 'PERCENTAGE' ? (
              <NumberInput
                {...inputProps}
                {...fieldProps.field}
                label={t(`metaData:${metaData.code?.toUpperCase()}`) || ''}
                autoFocus={metaData.isNew}
                disabled={metaData.accessibility !== MetaDataAccess.Editable || disabled}
                type={TYPE_PERCENTAGE}
                value={Number(metaData.value) || 0}
                onChange={({ numberValue }) => fieldProps.form.setFieldValue(fieldProps.field.name, numberValue)}
                isClearable={!disabled}
              />
            ) : metaDataType === 'DATE' ? (
              <FormikDatePicker
                {...{
                  ...fieldProps,
                  field: {
                    ...fieldProps.field,
                    value: parseMetaDataDate(fieldProps.field.value),
                  },
                }}
                disabled={metaData.accessibility !== MetaDataAccess.Editable || disabled}
                inputProps={{ ...inputProps }}
              />
            ) : (
              <Input
                {...inputProps}
                {...fieldProps.field}
                label={t(`metaData:${metaData.code?.toUpperCase()}`) || ''}
                autoFocus={metaData.isNew}
                disabled={metaData.accessibility !== MetaDataAccess.Editable || disabled}
                type={TYPE_TEXT}
                value={metaData.value || ''}
              />
            )}

            {!hideDeleteButton && (
              <MetaDataRemoveButton
                className={metaData.dataType === 'BOOL' ? 'buttonForCheckbox' : ''}
                onClick={handleRemoveClick}
                tooltip={t('tooltips.removeMetaData')}
                isModal={isModal}
                isDisabled={metaData.showOnCreate}
              >
                <Delete />
              </MetaDataRemoveButton>
            )}
          </ElementWithButton>
        );
      }}
    </Field>
  );
};

const MetaDataRemoveButton = styled(IconButton)<{ isModal?: boolean }>`
  margin-top: ${(props) => (props.isModal ? '25px' : '0px')};
`;
